import "@/App.css";
import { useEffect, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { trackPageView } from "@/lib/analytics";
import { lazyRetry as lazy, reloadOnChunkError } from "@/lib/lazyRetry";
import { SiteAnnouncement } from "@/components/SiteAnnouncement";
import { VerifyEmailBanner } from "@/components/VerifyEmailBanner";
import { API } from "@/lib/api";
import { CookieConsent } from "@/components/CookieConsent";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { LanguageProvider, useSeo, stripLang, ALT_LANGS } from "@/i18n";
import { AuthProvider } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Landing from "@/pages/Landing";

// Route-based code splitting: only the Landing page (LCP entry) is in the main
// bundle. Heavy libraries (recharts, leaflet, jsPDF) load on demand per route.
const Diagnostics = lazy(() => import("@/pages/Diagnostics"));
const History = lazy(() => import("@/pages/History"));
const Bufferbloat = lazy(() => import("@/pages/Bufferbloat"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const CustomPage = lazy(() => import("@/pages/CustomPage"));
const InstallApp = lazy(() => import("@/pages/InstallApp"));
const Rankings = lazy(() => import("@/pages/Rankings"));
const CountryRanking = lazy(() => import("@/pages/CountryRanking"));
const SeoHub = lazy(() => import("@/pages/SeoHub"));
const SeoStatPage = lazy(() => import("@/pages/SeoStatPage"));
const ContinentRanking = lazy(() => import("@/pages/ContinentRanking"));
const Login = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.Login })));
const Register = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.Register })));
const AuthCallback = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.AuthCallback })));
const ForgotPassword = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.ResetPassword })));
const VerifyEmail = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.VerifyEmail })));
const SharedResult = lazy(() => import("@/pages/SharedResult"));
const PublicProfile = lazy(() => import("@/pages/PublicProfile"));
const Stability = lazy(() => import("@/pages/Stability"));
const Mobile = lazy(() => import("@/pages/Mobile"));
const WebsiteSpeed = lazy(() => import("@/pages/WebsiteSpeed"));
const SeoAudit = lazy(() => import("@/pages/SeoAudit"));
const FullReport = lazy(() => import("@/pages/FullReport"));
const SharedReport = lazy(() => import("@/pages/SharedReport"));
const SavedSites = lazy(() => import("@/pages/SavedSites"));
const SiteDetail = lazy(() => import("@/pages/SiteDetail"));
const CompareSites = lazy(() => import("@/pages/CompareSites"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const GamePing = lazy(() => import("@/pages/GamePing"));
const GamePingIndex = lazy(() => import("@/pages/GamePing").then((m) => ({ default: m.GamePingIndex })));
const GuidePage = lazy(() => import("@/pages/Guides"));
const GuidesIndex = lazy(() => import("@/pages/Guides").then((m) => ({ default: m.GuidesIndex })));
const ReportBug = lazy(() => import("@/pages/ReportBug"));
const Sitemap = lazy(() => import("@/pages/Sitemap"));
const EmbedWidget = lazy(() => import("@/pages/EmbedWidget"));
const RankEmbed = lazy(() => import("@/pages/RankEmbed"));
const WidgetBuilder = lazy(() => import("@/pages/WidgetBuilder"));
const AccessibilityStatement = lazy(() => import("@/pages/Accessibility"));

const ROUTES = [
  { path: "/", el: <Landing /> },
  { path: "/test", el: <Diagnostics /> },
  { path: "/stability", el: <Stability /> },
  { path: "/mobile", el: <Mobile /> },
  { path: "/website-speed", el: <WebsiteSpeed /> },
  { path: "/seo-audit", el: <SeoAudit /> },
  { path: "/sites/audit", el: <FullReport /> },
  { path: "/sites/catalog", el: <SavedSites /> },
  { path: "/sites/compare", el: <CompareSites /> },
  { path: "/site/:host", el: <SiteDetail /> },
  { path: "/report/:id", el: <SharedReport /> },
  { path: "/bufferbloat", el: <Bufferbloat /> },
  { path: "/admin", el: <AdminPanel section="overview" /> },
  { path: "/admin/errors", el: <AdminPanel section="errors" /> },
  { path: "/admin/blocklist", el: <AdminPanel section="blocklist" /> },
  { path: "/admin/reports", el: <AdminPanel section="reports" /> },
  { path: "/admin/users", el: <AdminPanel section="users" /> },
  { path: "/admin/tests", el: <AdminPanel section="tests" /> },
  { path: "/admin/widget", el: <AdminPanel section="widget" /> },
  { path: "/admin/pages", el: <AdminPanel section="pages" /> },
  { path: "/admin/announcements", el: <AdminPanel section="announcements" /> },
  { path: "/admin/audit", el: <AdminPanel section="audit" /> },
  { path: "/admin/system", el: <AdminPanel section="system" /> },
  { path: "/admin/indexnow", el: <AdminPanel section="indexnow" /> },
  { path: "/rankings", el: <Rankings /> },
  { path: "/rankings/europe", el: <ContinentRanking /> },
  { path: "/rankings/asia", el: <ContinentRanking /> },
  { path: "/rankings/north-america", el: <ContinentRanking /> },
  { path: "/rankings/south-america", el: <ContinentRanking /> },
  { path: "/rankings/africa", el: <ContinentRanking /> },
  { path: "/rankings/oceania", el: <ContinentRanking /> },
  { path: "/rankings/:slug", el: <CountryRanking /> },
  { path: "/isp", el: <SeoHub /> },
  { path: "/isp/:cc/:slug", el: <SeoStatPage key="isp" kind="isp" /> },
  { path: "/city/:cc/:slug", el: <SeoStatPage key="city" kind="city" /> },
  { path: "/history", el: <History /> },
  { path: "/history/speed", el: <History section="speed" /> },
  { path: "/history/tests", el: <History section="speed" /> },
  { path: "/history/stability", el: <History section="stability" /> },
  { path: "/history/bufferbloat", el: <History section="bufferbloat" /> },
  { path: "/history/audit", el: <History section="audit" /> },
  { path: "/history/website-speed", el: <History section="website-speed" /> },
  { path: "/history/widget", el: <History section="widget" /> },
  { path: "/history/settings", el: <History section="settings" /> },
  { path: "/history/insights", el: <History section="insights" /> },
  { path: "/history/plan", el: <History section="plan" /> },
  { path: "/history/reports", el: <History section="reports" /> },
  { path: "/history/profile", el: <History section="profile" /> },
  { path: "/u/:username", el: <PublicProfile /> },
  { path: "/login", el: <Login /> },
  { path: "/register", el: <Register /> },
  { path: "/forgot-password", el: <ForgotPassword /> },
  { path: "/reset-password", el: <ResetPassword /> },
  { path: "/verify-email", el: <VerifyEmail /> },
  { path: "/r/:shareId", el: <SharedResult /> },
  { path: "/privacy", el: <Privacy /> },
  { path: "/terms", el: <Terms /> },
  { path: "/accessibility", el: <AccessibilityStatement /> },
  { path: "/gaming", el: <GamePingIndex /> },
  { path: "/gaming/:game", el: <GamePing /> },
  { path: "/guides", el: <GuidesIndex /> },
  { path: "/guides/:slug", el: <GuidePage /> },
  { path: "/p/:slug", el: <CustomPage /> },
  { path: "/install", el: <InstallApp /> },
  { path: "/report-bug", el: <ReportBug /> },
  { path: "/sitemap", el: <Sitemap /> },
  { path: "/widget", el: <WidgetBuilder /> },
  { path: "/embed", el: <EmbedWidget /> },
  { path: "/embed-ranking", el: <RankEmbed /> },
];

function Shell({ children }) {
  const loc = useLocation();
  const key = stripLang(loc.pathname);
  const bare = ["/login", "/register", "/forgot-password", "/reset-password", "/verify-email", "/embed", "/embed-ranking"].includes(key);
  if (bare) return children;
  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main-content" className="skip-link">{skipToContentLabel()}</a>
      <Navbar />
      <SiteAnnouncement />
      <VerifyEmailBanner />
      <main id="main-content" className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function skipToContentLabel() {
  try {
    const l = (localStorage.getItem("ns_lang") || "en").slice(0, 2);
    const m = { en: "Skip to content", pl: "Przejdź do treści", de: "Zum Inhalt springen", es: "Saltar al contenido", fr: "Aller au contenu", it: "Vai al contenuto", pt: "Ir para o conteúdo", tr: "İçeriğe geç", ru: "Перейти к содержимому", ar: "تخطي إلى المحتوى" };
    return m[l] || m.en;
  } catch { return "Skip to content"; }
}

// Old Polish slugs (pre-migration, PL was at root) -> new /pl/* equivalents.
// Only non-colliding Polish-named paths, so no current EN route is affected.
const OLD_PL_REDIRECTS = {
  "/mapa": "/pl/map",
  "/mapa-internetu": "/pl/map",
  "/ranking": "/pl/rankings",
  "/rankingi": "/pl/rankings",
  "/ranking-operatorow": "/pl/rankings",
  "/historia": "/pl/history",
  "/historia-testow": "/pl/history",
  "/test-predkosci": "/pl/test",
  "/test-predkosci-internetu": "/pl/test",
  "/predkosc": "/pl/test",
  "/stabilnosc": "/pl/stability",
  "/mobilny": "/pl/mobile",
  "/mobilne": "/pl/mobile",
  "/gry": "/pl/gaming",
  "/gaming-pl": "/pl/gaming",
  "/poradniki": "/pl/guides",
  "/prywatnosc": "/pl/privacy",
  "/polityka-prywatnosci": "/pl/privacy",
  "/regulamin": "/pl/terms",
  "/warunki": "/pl/terms",
  "/logowanie": "/pl/login",
  "/rejestracja": "/pl/register",
};

// Renamed routes -> keep old (already-indexed/bookmarked) URLs working, preserving lang prefix.
const PATH_REDIRECTS = {
  "/full-report": "/sites/audit",
  "/saved-sites": "/sites/catalog",
};

function NotFoundRedirect() {
  const { pathname } = useLocation();
  const clean = pathname.replace(/\/+$/, "").toLowerCase() || "/";
  if (OLD_PL_REDIRECTS[clean]) return <Navigate to={OLD_PL_REDIRECTS[clean]} replace />;
  const key = stripLang(clean);
  if (PATH_REDIRECTS[key]) {
    const prefix = clean.slice(0, clean.length - key.length);
    return <Navigate to={`${prefix}${PATH_REDIRECTS[key]}`} replace />;
  }
  const m = pathname.match(/^\/(pl|de|es)(\/|$)/);
  const home = m ? `/${m[1]}` : "/";
  return <Navigate to={home} replace />;
}

function PageFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center" aria-hidden="true">
      <span className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );
}

function Root() {
  const location = useLocation();
  const navigate = useNavigate();
  const routeKey = stripLang(location.pathname);
  useSeo(routeKey);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    // SPA route changes don't reset scroll; jump to top on each new page
    // (skip when there's an in-page hash anchor to preserve anchor scrolling).
    if (location.hash) return;
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    // Auto-detect language on first visit. English is the default (no prefix);
    // redirect to /pl, /de or /es when the browser language matches.
    const saved = localStorage.getItem("ns_lang");
    if (!saved && !location.pathname.match(/^\/(pl|de|es)(\/|$)/) && location.pathname !== "/auth/google") {
      const nl = (navigator.language || "").toLowerCase();
      let target = "en";
      if (nl.startsWith("pl")) target = "pl";
      else if (nl.startsWith("de")) target = "de";
      else if (nl.startsWith("es")) target = "es";
      localStorage.setItem("ns_lang", target);
      if (target !== "en") {
        const dest = location.pathname === "/" ? `/${target}` : `/${target}${location.pathname}`;
        navigate(dest + location.search, { replace: true });
      }
    }
  }, []); // eslint-disable-line

  if (location.pathname === "/auth/google") return <Suspense fallback={<PageFallback />}><AuthCallback /></Suspense>;

  return (
    <Shell>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          {ROUTES.map((r) => (
            <Route key={r.path} path={r.path} element={r.el} />
          ))}
          {ALT_LANGS.flatMap((pfx) =>
            ROUTES.map((r) => (
              <Route key={`${pfx}${r.path}`} path={r.path === "/" ? `/${pfx}` : `/${pfx}${r.path}`} element={r.el} />
            ))
          )}
          <Route path="*" element={<NotFoundRedirect />} />
        </Routes>
      </Suspense>
    </Shell>
  );
}

function App() {
  useEffect(() => {
    const onError = (e) => reloadOnChunkError(e?.error || e?.message);
    const onRejection = (e) => reloadOnChunkError(e?.reason);
    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onRejection);

    const trackInstall = () => {
      if (localStorage.getItem("ns_pwa_installed")) return;
      localStorage.setItem("ns_pwa_installed", "1");
      const ua = (navigator.userAgent || "").toLowerCase();
      let platform = "other";
      if (/android/.test(ua)) platform = "android";
      else if (/iphone|ipad|ipod/.test(ua) || (/(mac)/.test(ua) && navigator.maxTouchPoints > 1)) platform = "ios";
      else if (/win/.test(ua)) platform = "windows";
      else if (/mac/.test(ua)) platform = "mac";
      else if (/linux|cros/.test(ua)) platform = "linux";
      fetch(`${API}/pwa/installed`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ platform }) }).catch(() => {});
    };
    const onInstalled = () => trackInstall();
    window.addEventListener("appinstalled", onInstalled);
    // iOS/Safari never fire appinstalled: count a launch in standalone mode once.
    const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
    if (standalone) trackInstall();

    return () => {
      window.removeEventListener("error", onError, true);
      window.removeEventListener("unhandledrejection", onRejection);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <ErrorBoundary>
              <Root />
            </ErrorBoundary>
            <Toaster position="top-center" richColors />
            <CookieConsent />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
